import { Alert, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { mapStackTrace } from "sourcemapped-stacktrace";
import { submitError, type SubmitErrorResult } from "../api/api";
import { platform, version } from "../config";
import { usePortalApi } from "../db/portal-api";
import { useLicense } from "../license";
import { logger } from "../log";
import { ErrorLike } from "../types";
import { useCapgoStore } from "../update/capgo/store";
import { useWakeLock } from "../wakelock";

const log = logger("error");

export default function ReportError({ error }: { error: ErrorLike }) {
  const state = useErrorReporter(error);

  switch (state.state) {
    case "pending":
      return (
        <Alert severity="info" icon={<CircularProgress size="1em" />}>
          <Typography>Fehlerbericht wird gesendet…</Typography>
        </Alert>
      );
    case "reported":
      return (
        <Alert severity="info">
          <strong>Fehlerbericht gesendet.</strong>
          <br />
          Bitte teilen Sie uns die folgende Fehler-ID mit, wenn Sie Ihren Fehler
          melden:
          <br />
          <code>{state.result.id}</code>
        </Alert>
      );
    case "error":
      return (
        <Alert severity="error">
          Fehlerbericht konnte nicht gesendet werden.
        </Alert>
      );
  }
}

type ErrorReporterState =
  | { state: "pending" }
  | { state: "reported"; result: SubmitErrorResult }
  | { state: "error"; error: unknown };

function useErrorReporter(error: ErrorLike): ErrorReporterState {
  const reported = useRef(false);
  const [state, setState] = useState<ErrorReporterState>({ state: "pending" });
  const stack = useStack(error);
  const license = useLicense();
  const wakeLock = useWakeLock();
  const { i18n } = useTranslation();
  const capgo = useCapgoProperties();
  const portalApi = usePortalApi();

  const doSubmitError = useCallback(async () => {
    if (reported.current || !stack || !capgo) return;
    reported.current = true;

    try {
      const response = await submitError({
        body: {
          platform,
          version,
          license: license?.id,
          properties: {
            portalApiType: portalApi.type,
            portalApiBaseUrl: portalApi.baseUrl,
            useragent: navigator.userAgent,
            "i18n:language": i18n.language,
            "wakelock:active": wakeLock.active,
            "wakelock:type": wakeLock.type,
            ...capgo,
          },
          error: {
            message: error.message,
            stack: stack.join("\n"),
            code: error.code,
          },
        },
        throwOnError: true,
      });

      setState({ state: "reported", result: response.data });
    } catch (error) {
      log.error("Error submitting error", error);
      setState({ state: "error", error });
    }
  }, [capgo, error, i18n.language, license, stack, wakeLock]);

  useEffect(() => {
    doSubmitError();
  }, [doSubmitError]);

  return state;
}

function useStack(error: ErrorLike) {
  const [stack, setStack] = useState<string[]>();

  useEffect(() => {
    if (error.stack) {
      try {
        mapStackTrace(error.stack, setStack);
      } catch (e) {
        console.error("Error mapping stack trace:", e);
        setStack(error.stack.split("\n"));
      }
    } else {
      setStack([]);
    }
  }, [error.stack]);

  return stack;
}

function useCapgoProperties(): Record<string, unknown> | undefined {
  const { current, channel, deviceId, latest, builtinVersion, pluginVersion } =
    useCapgoStore();

  const values = {
    current,
    channel,
    deviceId,
    latest,
    builtinVersion,
    pluginVersion,
  };

  if (platform === "web") return {};
  if (Object.values(values).some((v) => v.state === "loading")) return;

  return Object.fromEntries(
    Object.entries(values)
      .filter(([, v]) => v.data !== undefined)
      .map(([k, v]) => [`capgo:${k}`, v.data]),
  );
}
